import React, { useCallback, useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { INumberDictionary, IStore } from '../../interfaces';
import * as actions from '../../actions';
import { Helmet } from 'react-helmet';
import * as L from 'leaflet';
import queryString from 'query-string';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import TrophyTooltip from './TrophyTooltip';
import { ConfederationsEnum, EnumSection, ICenterOfCountry, ITeam, ITrophyTooltip } from '../../interfaces/clientInterfaces';
import { Localizer } from '../../utils/localizer';
import { Countries } from '../../utils/countries';
import ReserveTeamTooltip from './ReserveTeamTooltip';
import { useAppDispatch } from '../../utils/modules';
import { Teams } from '../../utils/teams';

const MapModuleContainer = styled.div`
	flex: 1;
	display: flex;
	flex-direction: row;
	width: 100%;
	height: 100%;
`;

const MapContainer = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
`;

const MapFlexContainer = styled.div`
	display: flex; 
	flex: 1;
`;

const ClusterIcon = styled.span`
	font-family: Champions;
	font-size: 13px;
	text-shadow: 2px 2px 2px gold;
	color: black;
	img {
		height: 20px;
	}
	div {
		width: 120px;
		left: -30px;
		position: relative;
	}
`;

export default function ScarfMap() {

	const dispatch = useAppDispatch();

	useEffect(() => {
		dispatch(actions.setSectionName(EnumSection.map));
	}, [dispatch]);

	const isMobileViewActive = useSelector(
		(completeState: IStore): boolean => {
			return completeState.ui.isMobileViewActive;
		});

	const teams = useSelector(
		(completeState: IStore): ITeam[] => {
			return completeState.teams.teams;
		});

	const stadiumCluster = useSelector(
		(completeState: IStore): INumberDictionary<number[]> => {
			return completeState.map.stadiumCluster;
		});
	
	const trophyTooltip = useSelector(
		(completeState: IStore): ITrophyTooltip | null => {
			return completeState.ui.trophyTooltip;
		});

	const teamStadiumCoordinatesMap = useSelector(
		(completeState: IStore): Map<string, number[]> => {
			return completeState.map.teamStadiumCoordinatesMap;
		});

	const renderClusterIcon = useCallback(
		(title: string, icon: string, markersLength: number): React.ReactElement => {
			return (
				<ClusterIcon>
					<img src={`../../../img/flag/${icon}.png`} alt={title} />
					<div>{`${title}: ${markersLength} teams`}</div>
				</ClusterIcon>
			);
		},
		[]
	);

	const setTeamMarker = useCallback(
		(
			team: ITeam,
			stadiumCluster: INumberDictionary<number[]>
		) => {
			const iconUrl = `../../../img/shield/${team.countryCode}_${team.teamCode}.png`;
			const iconAnchor = Teams.getIconAnchorInStadiumCluster(team, stadiumCluster);
			const icon = L.icon({
				iconUrl,
				iconAnchor, // point of the icon which will correspond to marker's location
			});
			return L.marker(
				[team.stadium.north, team.stadium.east],
				{
					icon,
					id_test: team.teamId,
					riseOnHover: true,
				})
				// .addTo(scarvesMap)	
				.on('click', () => {
					dispatch(actions.setHomeDetailTeam(team.teamId));
				});

		},
		[dispatch]
	);

	useEffect(() => {
		const container = L.DomUtil.get('mapId');
		if (container != null) {
			container._leaflet_id = null;
		}
		let centerAt = null;
		const parsedQuery = queryString.parse(window.location.search);
		for (const key of Object.keys(parsedQuery)) {
			if (key.toLowerCase() === 'center') {
				centerAt = parsedQuery[key];
			}
		}
		const teamToCenterAt = teamStadiumCoordinatesMap.get(centerAt);
		const center: ICenterOfCountry = !teamToCenterAt 
			? Countries.getCenterOfCountry(centerAt || '')
			: {
				center: teamToCenterAt,
				mobileZoom: 10,
				zoom: 10
			};
		const scarvesMap = L.map('mapId').setView(center.center, isMobileViewActive ? center.mobileZoom : center.zoom);
		L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
			attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
			maxZoom: 11,
			id: 'mapbox/streets-v11',
			tileSize: 512,
			zoomOffset: -1,
			accessToken: 'pk.eyJ1IjoiYWdhcmNhaCIsImEiOiJjazhieG50NnYwMDRvM2VwNmZjZHd1aDBzIn0.GKhS7ajz4tM51I_pplBlKA'
		}).addTo(scarvesMap);
		const clusterGroups: { title: string, markers: any[], icon: string }[] = [];
		const clusterGroupsIndices: Map<string, number> = new Map<string, number>();
		const array: any[] = []; // TODO and add priority
		const orderedTeams = teams.sort((a, b) => {
			if (!a.category) {
				if (!b.category) {
					return 0;
				}
				return -1;
			}
			if (!b.category) {
				return 1;
			}
			if (a.category > b.category) {
				return -1;
			} else {
				return 1;
			}
		});
		for (const team of orderedTeams) {
			array.push(setTeamMarker(team, stadiumCluster));
			const localizedCountryName = Localizer.getLocalizedCountryName(team.countryCode);
			const clusterIndex = clusterGroupsIndices.get(localizedCountryName);
			if (clusterIndex !== undefined) {
				clusterGroups[clusterIndex].markers.push(
					setTeamMarker(team, stadiumCluster)
				);
			} else {
				clusterGroups.push({
					title: localizedCountryName,
					icon: team.countryCode,
					markers: [setTeamMarker(team, stadiumCluster)]
				});
				clusterGroupsIndices.set(localizedCountryName, clusterGroups.length -1);
			}
		}
		for (const cluster of clusterGroups) {
			const confederationOfCountryCode = Countries.getConfederationOfCountry(cluster.icon);
			let disableClusteringAtZoom = isMobileViewActive ? 6 : 7;
			if (
				ConfederationsEnum.sa === confederationOfCountryCode ||
				ConfederationsEnum.na === confederationOfCountryCode ||
				ConfederationsEnum.as === confederationOfCountryCode) {
				disableClusteringAtZoom = isMobileViewActive ? 3 : 4;
			} else if (
				ConfederationsEnum.af === confederationOfCountryCode ||
				cluster.icon === 'RU' ||
				cluster.icon === 'KZ'
			) {
				disableClusteringAtZoom = isMobileViewActive ? 4 : 5;
			}
			var markers = L.markerClusterGroup({
				maxClusterRadius: 50000,
				disableClusteringAtZoom,
				spiderfyDistanceMultiplier: 1.25,
				iconCreateFunction: (x: any) => {
					return L.divIcon({ html: ReactDOMServer.renderToString(renderClusterIcon(cluster.title, cluster.icon, cluster.markers.length)) });
				}
			});
			for (const marker of cluster.markers) {
				if (marker) {
					markers.addLayer(marker);
				}
			}
			scarvesMap.addLayer(markers);
		}
	}, [isMobileViewActive, renderClusterIcon, setTeamMarker, stadiumCluster, teamStadiumCoordinatesMap, teams]);

	return (
		<>
			<Helmet>
				<title>{'Scarf map - FootballScarfMuseumCollection'}</title>
			</Helmet>
			<MapModuleContainer>
				{trophyTooltip && <TrophyTooltip
					trophyTooltip={trophyTooltip}
				/>}
				<ReserveTeamTooltip/>
				<MapFlexContainer>
					<MapContainer id='mapId'/>
				</MapFlexContainer>
			</MapModuleContainer>
		</>
	);
}

